import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import env from './loadEnv';

/**
 * Initialize Sentry for error tracking in the Ionic app
 */
export const initSentry = () => {
  // Verify DSN is available
  if (!env.VITE_SENTRY_DSN) {
    console.error('Sentry DSN is missing. Please check your .env file');
    return;
  }

  try {
    // Initialize Sentry with mobile-first configuration
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,

      // Core integrations for React and performance monitoring
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: true,
        }),
        Sentry.browserTracingIntegration(),
      ],

      // Performance monitoring - capture all transactions in development
      tracesSampleRate: 1.0,

      // Session replay settings - 100% in development, 10% in production
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,

      // Enable debug mode for development
      debug: false,

      // Ensure all events are captured
      sampleRate: 1.0,

      // Allow sending from development environments
      allowUrls: ['localhost', '127.0.0.1', '192.168.1.172', '192.168.1.120'],

      // Set environment based on platform for better organization in Sentry
      environment: Capacitor.isNativePlatform()
        ? (Capacitor.getPlatform() === 'ios' ? 'ios' : 'android')
        : 'web',

      // Log all sent events for debugging
      beforeSend: (event) => {
        console.log('Sending event to Sentry:', event.event_id);
        return event;
      },
    });

    // Add a breadcrumb to confirm initialization
    Sentry.addBreadcrumb({
      category: 'initialization',
      message: 'Sentry initialized successfully',
      level: 'info',
    });

    return true;
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
    return false;
  }
};

/**
 * Set user information in Sentry
 * @param userId User ID
 * @param email User email
 * @param username User name
 */
export const setSentryUser = (userId: string, email?: string, username?: string) => {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
};

/**
 * Clear user information from Sentry (on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Set a custom tag in Sentry
 * @param key Tag key
 * @param value Tag value
 */
export const setSentryTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

/**
 * Capture an exception in Sentry
 * @param error Error object
 * @param context Additional context
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, { extra: context });
};
